
import React, { useCallback } from 'react'; // Fix: Ensure import from React
import { Profile } from '@/types/auth';
import { supabase } from '@/integrations/supabase/client';
import { getCachedProfile, setCachedProfile } from '@/utils/profile-cache';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'FetchProfile' });

// Defensive: log an error if useCallback is not defined (for debug visibility)
if (typeof useCallback !== 'function') {
  // eslint-disable-next-line no-console
  console.error('React useCallback is undefined! Are you running in a React context?');
}

export const useFetchProfile = (dispatch: Function) => {
  return useCallback(async (userId: string, retryCount = 0, skipCache = false): Promise<Profile | null> => {
    try {
      if (!userId) {
        log.warn('Cannot fetch profile: Missing userId');
        return null;
      }
      
      // First check cache (unless skipCache is true)
      if (!skipCache) {
        const cachedProfile = getCachedProfile(userId);
        if (cachedProfile) {
          log.info('✅ Using cached profile for:', userId);
          dispatch({ type: 'SET_PROFILE', payload: cachedProfile });
          return cachedProfile;
        }
      } else {
        log.info('Skipping cache, fetching fresh profile for:', userId);
      }

      // If no cache (or skipCache), fetch from Supabase - Use a simple query without dot notation
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        log.error('Profile fetch error (supabase):', profileError);
        throw profileError;
      }

      if (profile) {
        log.info('✅ Fetched fresh profile for:', userId, 'Profile:', profile);
        setCachedProfile(userId, profile);
        dispatch({ type: 'SET_PROFILE', payload: profile });
        return profile;
      }

      // Profile is null, so log this fact
      log.info('Profile fetch: no profile found for user:', userId);

      return null;
    } catch (error) {
      log.error('Profile fetch error:', error);
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        log.info(`Retrying profile fetch in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return useFetchProfile(dispatch)(userId, retryCount + 1);
      }
      // Log that we are giving up
      log.error('Profile fetch: giving up after retries for user:', userId);
      throw error;
    }
  }, [dispatch]);
};
